import { createSelector } from 'reselect';
import { List as ImmutableList, Map as ImmutableMap, is } from 'immutable';
import { me } from '../initial_state';

const getAccountBase         = (state, id) => state.getIn(['accounts', id], null);
const getAccountCounters     = (state, id) => state.getIn(['accounts_counters', id], null);
const getAccountRelationship = (state, id) => state.getIn(['relationships', id], null);
const getAccountMoved        = (state, id) => state.getIn(['accounts', state.getIn(['accounts', id, 'moved'])]);

export const makeGetAccount = () => {
  return createSelector([getAccountBase, getAccountCounters, getAccountRelationship, getAccountMoved], (base, counters, relationship, moved) => {
    if (base === null) {
      return null;
    }

    return base.merge(counters).withMutations(map => {
      map.set('relationship', relationship);
      map.set('moved', moved);
    });
  });
};

const toServerSideType = columnType => {
  switch (columnType) {
  case 'home':
  case 'notifications':
  case 'public':
  case 'thread':
  case 'account':
    return columnType;
  default:
    if (columnType.indexOf('list:') > -1) {
      return 'home';
    } else {
      return 'public'; // community, account, hashtag
    }
  }
};

const escapeRegExp = string =>
  string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

const regexFromFilters = filters => {
  if (filters.size === 0) {
    return null;
  }

  return new RegExp(filters.map(filter => {
    let expr = escapeRegExp(filter.get('phrase'));

    if (filter.get('whole_word')) {
      if (/^[\w]/.test(expr)) {
        expr = `\\b${expr}`;
      }

      if (/[\w]$/.test(expr)) {
        expr = `${expr}\\b`;
      }
    }

    return expr;
  }).join('|'), 'i');
};

// Memoize the filter regexps for each valid server contextType
const makeGetFiltersRegex = () => {
  let memo = {};

  return (state, { contextType }) => {
    if (!contextType) return ImmutableList();

    const serverSideType = toServerSideType(contextType);
    const filters = state.get('filters', ImmutableList()).filter(filter => filter.get('context').includes(serverSideType) && (filter.get('expires_at') === null || Date.parse(filter.get('expires_at')) > (new Date())));

    if (!memo[serverSideType] || !is(memo[serverSideType].filters, filters)) {
      const dropRegex = regexFromFilters(filters.filter(filter => filter.get('irreversible')));
      const regex = regexFromFilters(filters);
      memo[serverSideType] = { filters: filters, results: [dropRegex, regex] };
    }
    return memo[serverSideType].results;
  };
};

export const getFiltersRegex = makeGetFiltersRegex();

export const makeGetStatus = () => {
  return createSelector(
    [
      (state, { id }) => state.getIn(['statuses', id]),
      (state, { id }) => state.getIn(['statuses', state.getIn(['statuses', id, 'reblog'])]),
      (state, { id }) => state.getIn(['accounts', state.getIn(['statuses', id, 'account'])]),
      (state, { id }) => state.getIn(['accounts', state.getIn(['statuses', state.getIn(['statuses', id, 'reblog']), 'account'])]),
      (state, { id }) => state.getIn(['statuses', state.getIn(['statuses', id, 'reblog']), 'following_accounts', 'accounts'])?.map(obj => state.getIn(['accounts', obj.get('id')])),
      (state, { id }) => state.getIn(['statuses', id, 'following_accounts', 'accounts'])?.map(obj => state.getIn(['accounts', obj.get('id')])),
      getFiltersRegex,
    ],

    (statusBase, statusReblog, accountBase, accountReblog, rebloggedFollowingAccounts, followingAccounts, filtersRegex) => {
      if (!statusBase) {
        return null;
      }

      if(statusBase.get('replies_count') > 0) {
        statusBase = statusBase.withMutations(map => map.setIn(['following_accounts', 'accounts'], followingAccounts));
      } else if (statusBase.get('favourites_count') > 0) {
        statusBase = statusBase.withMutations(map => map.setIn(['following_accounts', 'accounts'], followingAccounts));
      }

      if (statusReblog) {
        statusReblog = statusReblog.withMutations(map => map.set('account', accountReblog).setIn(['following_accounts', 'accounts'], rebloggedFollowingAccounts));
      } else {
        statusReblog = null;
      }

      const dropRegex = (accountReblog || accountBase).get('id') !== me && filtersRegex[0];
      if (dropRegex && dropRegex.test(statusBase.get('reblog') ? statusReblog.get('search_index') : statusBase.get('search_index'))) {
        return null;
      }

      const regex     = (accountReblog || accountBase).get('id') !== me && filtersRegex[1];
      const filtered  = regex && regex.test(statusBase.get('reblog') ? statusReblog.get('search_index') : statusBase.get('search_index'));

      return statusBase.withMutations(map => {
        map.set('reblog', statusReblog);
        map.set('account', accountBase);
        map.set('filtered', filtered);
      });
    },
  );
};

export const makeGetChatMessage = () => {
  return createSelector(
    [
      (state) => state,
      (state, { id }) => state.getIn(['chat_messages', id]),
      (state, { id }) => state.getIn(['accounts', `${state.getIn(['chat_messages', `${id}`, 'from_account_id'])}`]),
      (state, { id }) => state.getIn(['accounts', `${state.getIn(['chat_messages', `${id}`, 'origin_message', 'from_account_id'])}`]),
    ],
    (state, base, account, origin_message_account) => {
      if (!base) return null;
      return base.withMutations((map) => {
        map.set('account', account);
        if (origin_message_account) {
          map.setIn(['origin_message', 'account'], origin_message_account);
        }
      });
    },
  );
};

export const makeGetChatConversation = () => {
  return createSelector(
    [
      (state) => state,
      (state, { id }) => state.getIn(['chat_conversations', `${id}`]),
      (state) => state.get('accounts'),
    ],
    (state, base, allAccounts) => {
      if (!base) return null;

      let otherAccounts = ImmutableList();
      if (allAccounts) {
        const other_account_ids = base.get('other_account_ids');
        if (other_account_ids) {
          other_account_ids.forEach((acctId) => {
            const acct = allAccounts.get(`${acctId}`, null);
            if (acct) {
              otherAccounts = otherAccounts.set(otherAccounts.size, acct);
            }
          });
        }
      }

      return base.withMutations((map) => {
        map.set('other_accounts', otherAccounts);
      });
    },
  );
};

export const makeGetPictureInPicture = () => {
  return createSelector([
    (state, { id }) => state.get('picture_in_picture').statusId === id,
    (state) => state.getIn(['meta', 'layout']) !== 'mobile',
  ], (inUse, available) => ImmutableMap({
    inUse: inUse && available,
    available,
  }));
};

const getAlertsBase = state => state.get('alerts');

export const getAlerts = createSelector([getAlertsBase], (base) => {
  let arr = [];

  base.forEach(item => {
    arr.push({
      message: item.get('message'),
      message_values: item.get('message_values'),
      title: item.get('title'),
      key: item.get('key'),
      dismissAfter: 5000,
      barStyle: {
        zIndex: 200,
      },
    });
  });

  return arr;
});

export const makeGetNotification = () => {
  return createSelector([
    (_, base)             => base,
    (state, _, accountId) => state.getIn(['accounts', accountId]),
  ], (base, account) => {
    return base.set('account', account);
  });
};

export const getAccountGallery = createSelector([
  (state, id) => state.getIn(['timelines', `account:${id}:media`, 'items'], ImmutableList()),
  state       => state.get('statuses'),
  (state, id) => state.getIn(['accounts', id]),
], (statusIds, statuses, account) => {
  let medias = ImmutableList();

  statusIds.forEach(statusId => {
    const status = statuses.get(statusId);
    medias = medias.concat(status.get('media_attachments').map(media => media.set('status', status).set('account', account)));
  });

  return medias;
});
